import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, UserRound, ClipboardList, Star, Handshake, CalendarDays } from "lucide-react";
import { SEO } from "../components/SEO";
import { API } from "../lib/site";
import { MEMBER_TOKEN_KEY } from "./MemberRegister";
import { StarRow } from "../components/ReviewsPreview";

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return iso;
    }
};

const MemberDashboard = () => {
    const [member, setMember] = useState(null);
    const navigate = useNavigate();
    const token = localStorage.getItem(MEMBER_TOKEN_KEY);
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    const logout = useCallback(() => {
        localStorage.removeItem(MEMBER_TOKEN_KEY);
        navigate("/login?tab=member");
    }, [navigate]);

    useEffect(() => {
        if (!token) {
            navigate("/login?tab=member");
            return;
        }
        axios
            .get(`${API}/members/me`, authHeaders)
            .then((res) => setMember(res.data))
            .catch((err) => {
                if (err.response?.status === 401) logout();
                else toast.error("Could not load your account.");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!member) {
        return (
            <div className="grain flex min-h-screen items-center justify-center bg-[#04150D]">
                <p className="text-sm text-[#FDFBF7]/60">Loading your account…</p>
            </div>
        );
    }

    return (
        <main>
            <SEO title="My Account | Global Green Hotel" description="Your GGH member account." />
            <div className="grain min-h-screen bg-[#04150D] py-28">
                <div className="relative z-[2] mx-auto max-w-[1000px] px-5 sm:px-8">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <p className="eyebrow-gold">GGH Member</p>
                            <h1 className="font-display mt-2 text-3xl font-medium text-[#FDFBF7] sm:text-4xl" data-testid="member-welcome">
                                Welcome, {member.name}
                            </h1>
                            <p className="mt-1 text-sm text-[#FDFBF7]/50">
                                {member.email}
                                {member.country ? ` · ${member.country}` : ""}
                            </p>
                        </div>
                        <button type="button" data-testid="member-logout" onClick={logout} className="btn-outline-light !px-5 !py-2.5">
                            <LogOut size={15} aria-hidden="true" /> Sign Out
                        </button>
                    </div>

                    <div className="mt-10 grid gap-6 lg:grid-cols-3">
                        <section className="rounded-2xl border border-white/10 bg-[#07211A] p-7" data-testid="member-profile-card">
                            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">
                                <UserRound size={14} aria-hidden="true" /> My Profile
                            </p>
                            <dl className="mt-5 space-y-3 text-sm">
                                <div>
                                    <dt className="text-[10px] font-extrabold uppercase tracking-wider text-[#FDFBF7]/40">Name</dt>
                                    <dd className="text-[#FDFBF7]">{member.name}</dd>
                                </div>
                                <div>
                                    <dt className="text-[10px] font-extrabold uppercase tracking-wider text-[#FDFBF7]/40">Email</dt>
                                    <dd className="text-[#FDFBF7]">{member.email}</dd>
                                </div>
                                <div>
                                    <dt className="text-[10px] font-extrabold uppercase tracking-wider text-[#FDFBF7]/40">Member since</dt>
                                    <dd className="inline-flex items-center gap-1.5 text-[#FDFBF7]">
                                        <CalendarDays size={13} className="text-[#C2A160]" aria-hidden="true" />
                                        {formatDate(member.created_at)}
                                    </dd>
                                </div>
                            </dl>
                        </section>

                        <section className="rounded-2xl border border-white/10 bg-[#07211A] p-7 lg:col-span-2" data-testid="member-activity-card">
                            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">
                                <ClipboardList size={14} aria-hidden="true" /> My Involvement
                            </p>
                            {member.registrations.length === 0 && member.interests.length === 0 && member.reviews.length === 0 ? (
                                <div className="mt-5">
                                    <p className="text-sm leading-relaxed text-[#FDFBF7]/60">
                                        Nothing here yet. Register to join the movement, express an
                                        interest, or leave a review — your activity will appear here.
                                    </p>
                                    <Link to="/get-involved" data-testid="member-get-involved" className="btn-gold mt-5 !px-5 !py-2.5">
                                        Get Involved
                                    </Link>
                                </div>
                            ) : (
                                <ul className="mt-5 grid gap-3">
                                    {member.registrations.map((r) => (
                                        <li key={r.id} data-testid={`member-registration-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#04150D]/40 px-5 py-4">
                                            <div>
                                                <p className="text-sm font-bold text-[#FDFBF7]">Movement registration — {r.reason}</p>
                                                <p className="mt-0.5 text-xs text-[#FDFBF7]/50">
                                                    {(r.contributions || []).join(", ")} · {formatDate(r.created_at)}
                                                </p>
                                            </div>
                                            <Handshake size={16} className="text-[#C2A160]" aria-hidden="true" />
                                        </li>
                                    ))}
                                    {member.interests.map((r) => (
                                        <li key={r.id} data-testid={`member-interest-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#04150D]/40 px-5 py-4">
                                            <div>
                                                <p className="text-sm font-bold text-[#FDFBF7]">Interest — {r.interest_area}</p>
                                                <p className="mt-0.5 text-xs text-[#FDFBF7]/50">{formatDate(r.created_at)}</p>
                                            </div>
                                        </li>
                                    ))}
                                    {member.reviews.map((r) => (
                                        <li key={r.id} data-testid={`member-review-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#04150D]/40 px-5 py-4">
                                            <div>
                                                <div className="flex items-center gap-3">
                                                    <p className="text-sm font-bold text-[#FDFBF7]">My review</p>
                                                    <StarRow rating={r.rating} size={13} />
                                                </div>
                                                <p className="mt-0.5 text-xs text-[#FDFBF7]/50">
                                                    {r.approved ? "Published" : "Pending review by the GGH team"} · {formatDate(r.created_at)}
                                                </p>
                                            </div>
                                            <Star size={16} className="text-[#C2A160]" aria-hidden="true" />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>

                </div>
            </div>
        </main>
    );
};

export default MemberDashboard;
